import { type FC } from 'react';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

const CookiePolicyPage: FC = () => {
  usePageMeta({ title: 'Cookie Policy', description: 'How Quanthop uses cookies and similar technologies on our platform.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-[680px] mx-auto relative z-10 text-center">
          <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
            Cookie Policy
          </h1>

          <p className="text-[15px] text-gray-300/70 leading-[1.7]">
            How Quanthop uses cookies and similar technologies.
          </p>

          <p className="text-[13px] text-gray-400/60 mt-6">
            Last updated: 8 March 2026
          </p>
        </div>
      </section>

      {/* ════════════════ CONTENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[720px] mx-auto text-gray-700 text-[15px] leading-[1.8]">
          {/* ── 1 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            1. What Are Cookies
          </h2>
          <p className="mb-4">
            Cookies are small text files stored on your device when you visit a
            website.
          </p>
          <p>
            They help websites function properly and improve the user
            experience.
          </p>

          {/* ── 2 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            2. Types of Cookies We Use
          </h2>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Essential Cookies
          </h3>
          <p className="mb-3">
            These cookies are necessary for the platform to function. Examples
            include:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>authentication cookies</li>
            <li>session management</li>
            <li>security verification</li>
          </ul>
          <p>
            Without these cookies the Service cannot operate properly.
          </p>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Analytics Cookies
          </h3>
          <p className="mb-3">
            These cookies help us understand how users interact with the
            platform. They may collect information such as:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>pages visited</li>
            <li>session duration</li>
            <li>browser type</li>
            <li>device information</li>
          </ul>
          <p>This data is used to improve the platform.</p>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Preference Cookies
          </h3>
          <p className="mb-3">
            These cookies store user preferences such as:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>interface settings</li>
            <li>display preferences</li>
          </ul>

          {/* ── 3 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            3. Managing Cookies
          </h2>
          <p className="mb-3">
            Most browsers allow you to control cookies through browser
            settings. You can:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>block cookies</li>
            <li>delete existing cookies</li>
            <li>receive alerts when cookies are used</li>
          </ul>
          <p>
            Disabling certain cookies may affect platform functionality.
          </p>

          {/* ── 4 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            4. Third-Party Cookies
          </h2>
          <p className="mb-4">
            Some third-party services may set cookies when interacting with the
            platform.
          </p>
          <p>
            These services may include analytics providers or payment
            processors.
          </p>

          {/* ── 5 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            5. Updates
          </h2>
          <p className="mb-4">
            This Cookie Policy may be updated periodically.
          </p>
          <p>Changes will be posted on this page.</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default CookiePolicyPage;
